import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import SimpleCanvas from '../components/SimpleCanvas';
import BrushableImage from '../components/BrushableImage';
import { Card, Button, ProgressBlinker } from '../components/ui';
import { inpaintImage, generateSamMasks } from '../api';
import { googleImageSearch } from '../googleImageSearch';

const CANVAS_WIDTH = 1280;
const CANVAS_HEIGHT = 720;
const CANVAS_ASPECT_RATIO = `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`;

function SimpleMainPage() {
  // Canvas components state
  const [canvasComponents, setCanvasComponents] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Text inputs
  const [backgroundPrompt, setBackgroundPrompt] = useState('');
  const [backgroundResults, setBackgroundResults] = useState([]);
  const [expandedBackgroundIndex, setExpandedBackgroundIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Image search results
  const [searchResults, setSearchResults] = useState([]);
  
  // Component extraction state
  const [selectedImage, setSelectedImage] = useState(null); // Current image being processed
  const [imageSource, setImageSource] = useState(null); // 'search' or 'upload' or 'drag'
  const [brushMode, setBrushMode] = useState(false);
  const [brushSize, setBrushSize] = useState(8);
  const [brushSizePx, setBrushSizePx] = useState(0);
  const [brushStrokes, setBrushStrokes] = useState([]);
  
  // SAM mask state
  const [samMasks, setSamMasks] = useState([]);
  const [selectedSamMasks, setSelectedSamMasks] = useState([]);

  // Component cache for extracted components
  const [componentCache, setComponentCache] = useState([]);

  const canvasRef = useRef(null);

  const getCanvasDimensions = useCallback(() => {
    const canvasElement = canvasRef.current;
    if (canvasElement) {
      const rect = canvasElement.getBoundingClientRect();
      const cssWidth = rect?.width || CANVAS_WIDTH;
      const cssHeight = rect?.height || CANVAS_HEIGHT;
      const pixelWidth = canvasElement.width || cssWidth;
      const pixelHeight = canvasElement.height || cssHeight;
      return { cssWidth, cssHeight, pixelWidth, pixelHeight };
    }
    return { cssWidth: CANVAS_WIDTH, cssHeight: CANVAS_HEIGHT, pixelWidth: CANVAS_WIDTH, pixelHeight: CANVAS_HEIGHT };
  }, []);

  const loadImageElement = (src) => new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No image selected'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image retrieval failed: try another image or upload your own'));
    img.src = src;
  });

  const computeBrushPixels = useCallback((units, width, height) => {
    if (!width || !height) {
      return Math.max(units, 1);
    }
    const reference = Math.min(width, height);
    const normalizedUnits = Math.min(Math.max(units, 1), 100);
    const pixels = (normalizedUnits / 100) * reference;
    const minPixels = Math.max(reference * 0.01, 3);
    return Math.max(pixels, minPixels);
  }, []);

  const computeStrokeBounds = (strokes, width, height) => {
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    const defaultBrushPx = computeBrushPixels(brushSize, width, height);
    strokes.forEach(stroke => {
      stroke.forEach(point => {
        const half = (point.size || defaultBrushPx) / 2;
        minX = Math.min(minX, point.x - half);
        minY = Math.min(minY, point.y - half);
        maxX = Math.max(maxX, point.x + half);
        maxY = Math.max(maxY, point.y + half);
      });
    });
    minX = Math.max(0, Math.floor(minX));
    minY = Math.max(0, Math.floor(minY));
    maxX = Math.min(width, Math.ceil(maxX));
    maxY = Math.min(height, Math.ceil(maxY));
    return { minX, minY, maxX, maxY };
  };

  const createMaskCanvas = (width, height, strokes) => {
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = width;
    maskCanvas.height = height;
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.clearRect(0, 0, width, height);
    maskCtx.fillStyle = 'white';
    maskCtx.strokeStyle = 'white';
    maskCtx.lineCap = 'round';
    maskCtx.lineJoin = 'round';

    const defaultBrushPx = computeBrushPixels(brushSize, width, height);

    strokes.forEach(stroke => {
      if (!stroke || stroke.length === 0) {
        return;
      }
      stroke.forEach((point, index) => {
        const radius = (point.size || defaultBrushPx) / 2;
        if (index > 0) {
          const prev = stroke[index - 1];
          maskCtx.beginPath();
          maskCtx.lineWidth = radius * 2;
          maskCtx.moveTo(prev.x, prev.y);
          maskCtx.lineTo(point.x, point.y);
          maskCtx.stroke();
        }
        maskCtx.beginPath();
        maskCtx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        maskCtx.fill();
      });
    });

    return maskCanvas;
  };

  const createMaskedComponent = async (imageSrc, strokes) => {
    const img = await loadImageElement(imageSrc);
    const { width, height } = img;
    const maskCanvas = createMaskCanvas(width, height, strokes);
    const maskedCanvas = document.createElement('canvas');
    maskedCanvas.width = width;
    maskedCanvas.height = height;
    const maskedCtx = maskedCanvas.getContext('2d');
    maskedCtx.drawImage(img, 0, 0);
    maskedCtx.globalCompositeOperation = 'destination-in';
    maskedCtx.drawImage(maskCanvas, 0, 0);

    const { minX, minY, maxX, maxY } = computeStrokeBounds(strokes, width, height);
    if (maxX <= minX || maxY <= minY) {
      throw new Error('Mask too small to extract component');
    }

    const cropWidth = Math.max(1, maxX - minX);
    const cropHeight = Math.max(1, maxY - minY);
    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cropWidth;
    croppedCanvas.height = cropHeight;
    const cropCtx = croppedCanvas.getContext('2d');
    cropCtx.drawImage(
      maskedCanvas,
      minX,
      minY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    return {
      dataUrl: croppedCanvas.toDataURL('image/png'),
      width: cropWidth,
      height: cropHeight
    };
  };

  // Reset error message after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Handle Google Image Search
  const handleImageSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setLoading(true);
      setLoadingMessage('Searching images...');
      const results = await googleImageSearch(searchQuery, 20);
      setSearchResults(results);
    } catch (error) {
      setErrorMessage('Error searching images: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Handle image selection (from upload)
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target.result);
      setImageSource('upload');
      // Reset extraction state
      setBrushStrokes([]);
      setSamMasks([]);
      setSelectedSamMasks([]);
    };
    reader.readAsDataURL(file);
  };

  // Handle drag and drop from library
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target.result);
        setImageSource('drag');
        // Reset extraction state
        setBrushStrokes([]);
        setSamMasks([]);
        setSelectedSamMasks([]);
      };
      reader.readAsDataURL(imageFile);
    }
  };

  // Add component to canvas
  const handleAddComponentToCanvas = (imageUrl, title = 'Component', options = {}) => {
    const defaultSize = 150;
    const MAX_SIZE_PERCENT = 0.25; // 25% of canvas
    const { cssWidth: canvasWidth, cssHeight: canvasHeight } = getCanvasDimensions();

    let width = options.width || defaultSize;
    let height = options.height || defaultSize;

    const maxArea = (canvasWidth * canvasHeight) * MAX_SIZE_PERCENT;
    const currentArea = width * height;

    if (currentArea > maxArea) {
      const scaleFactor = Math.sqrt(maxArea / currentArea);
      width = Math.round(width * scaleFactor);
      height = Math.round(height * scaleFactor);
      console.log(`Component resized from ${options.width}x${options.height} to ${width}x${height} (25% max canvas area)`);
    }

    // Use provided x, y coordinates if available, otherwise use random centered position
    const x = options.x !== undefined ? options.x : canvasWidth / 2 + (Math.random() - 0.5) * 200;
    const y = options.y !== undefined ? options.y : canvasHeight / 2 + (Math.random() - 0.5) * 200;

    const newComponent = {
      id: `component_${Date.now()}`,
      x,
      y,
      width,
      height,
      rotation: options.rotation || 0,
      component: {
        id: `comp_${Date.now()}`,
        image: imageUrl,
        label: title
      }
    };

    setCanvasComponents(prev => [...prev, newComponent]);
  };

  const handleApplyBackgroundToCanvas = useCallback((imageUrl) => {
    if (!imageUrl) return;
    const timestamp = Date.now();
    const { cssWidth: canvasWidth, cssHeight: canvasHeight } = getCanvasDimensions();
    const backgroundComponent = {
      id: `reve_background_${timestamp}`,
      x: canvasWidth / 2,
      y: canvasHeight / 2,
      width: canvasWidth,
      height: canvasHeight,
      rotation: 0,
      component: {
        id: `reve_background_${timestamp}`,
        image: imageUrl,
        label: 'REVE Background',
        type: 'reve_background'
      }
    };

    setCanvasComponents(prev => {
      const withoutExisting = prev.filter(comp => comp?.component?.type !== 'reve_background');
      return [backgroundComponent, ...withoutExisting];
    });
  }, [getCanvasDimensions, setCanvasComponents]);

  const downloadDataUrl = (dataUrl, filename) => {
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadBackgroundResult = useCallback((imageUrl) => {
    if (!imageUrl) return;
    downloadDataUrl(imageUrl, `reve-background-${Date.now()}.png`);
  }, []);

  const handleClearBackgroundResults = useCallback(() => {
    setBackgroundResults([]);
    setExpandedBackgroundIndex(null);
  }, []);

  const handleDownloadCanvas = () => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) {
      setErrorMessage('Canvas unavailable for download');
      return;
    }
    try {
      const dataUrl = canvasEl.getCleanDataURL
        ? canvasEl.getCleanDataURL()
        : canvasEl.toDataURL('image/png');
      downloadDataUrl(dataUrl, `canvas-${Date.now()}.png`);
    } catch (error) {
      console.error('Canvas download error:', error);
      setErrorMessage('Unable to download canvas');
    }
  };

  const handleDownloadMask = async () => {
    try {
      const { cssWidth, cssHeight, pixelWidth, pixelHeight } = getCanvasDimensions();
      const scaleX = pixelWidth / cssWidth;
      const scaleY = pixelHeight / cssHeight;

      const shapeCanvas = document.createElement('canvas');
      shapeCanvas.width = pixelWidth;
      shapeCanvas.height = pixelHeight;
      const shapeCtx = shapeCanvas.getContext('2d');
      if (!shapeCtx) {
        throw new Error('Mask context unavailable');
      }

      const objectComponents = canvasComponents.filter(
        (component) => component?.component?.type !== 'reve_background'
      );

      const images = await Promise.all(
        objectComponents.map(async (component) => {
          const src = component?.component?.image;
          if (!src) {
            return null;
          }
          try {
            return await loadImageElement(src);
          } catch (err) {
            console.warn('Failed to load component image for mask', err);
            return null;
          }
        })
      );

      shapeCtx.save();
      shapeCtx.scale(scaleX, scaleY);
      objectComponents.forEach((component, index) => {
        shapeCtx.save();
        shapeCtx.translate(component.x, component.y);
        const rotationRad = ((component.rotation || 0) * Math.PI) / 180;
        shapeCtx.rotate(rotationRad);
        const img = images[index];
        if (img) {
          shapeCtx.drawImage(
            img,
            -component.width / 2,
            -component.height / 2,
            component.width,
            component.height
          );
        } else {
          shapeCtx.fillStyle = '#000000';
          shapeCtx.fillRect(
            -component.width / 2,
            -component.height / 2,
            component.width,
            component.height
          );
        }
        shapeCtx.restore();
      });
      shapeCtx.restore();

      const shapeImageData = shapeCtx.getImageData(0, 0, pixelWidth, pixelHeight);
      const shapePixels = shapeImageData.data;
      const alphaThreshold = 20;

      for (let i = 0; i < shapePixels.length; i += 4) {
        const alpha = shapePixels[i + 3];
        if (alpha > alphaThreshold) {
          shapePixels[i] = 0;
          shapePixels[i + 1] = 0;
          shapePixels[i + 2] = 0;
          shapePixels[i + 3] = 255;
        } else {
          shapePixels[i] = 0;
          shapePixels[i + 1] = 0;
          shapePixels[i + 2] = 0;
          shapePixels[i + 3] = 0;
        }
      }
      shapeCtx.putImageData(shapeImageData, 0, 0);

      const blurCanvas = document.createElement('canvas');
      blurCanvas.width = pixelWidth;
      blurCanvas.height = pixelHeight;
      const blurCtx = blurCanvas.getContext('2d');
      if (!blurCtx) {
        throw new Error('Blur context unavailable');
      }
      const blurRadiusPx = Math.max(6, Math.round(Math.max(scaleX, scaleY) * 6));
      blurCtx.filter = `blur(${blurRadiusPx}px)`;
      blurCtx.drawImage(shapeCanvas, 0, 0);
      blurCtx.filter = 'none';

      // Get alpha channel from blurred shapes (before compositing)
      const blurImageData = blurCtx.getImageData(0, 0, pixelWidth, pixelHeight);
      const blurPixels = blurImageData.data;

      // Create final mask: invert alpha to get black objects on white background
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = pixelWidth;
      finalCanvas.height = pixelHeight;
      const finalCtx = finalCanvas.getContext('2d');
      if (!finalCtx) {
        throw new Error('Final context unavailable');
      }

      const finalImageData = finalCtx.createImageData(pixelWidth, pixelHeight);
      const finalPixels = finalImageData.data;

      for (let i = 0; i < blurPixels.length; i += 4) {
        const alpha = blurPixels[i + 3];
        // Invert alpha: high alpha (object) → black (0), low alpha (background) → white (255)
        const shade = Math.max(0, 255 - alpha);
        finalPixels[i] = shade;
        finalPixels[i + 1] = shade;
        finalPixels[i + 2] = shade;
        finalPixels[i + 3] = 255;
      }
      finalCtx.putImageData(finalImageData, 0, 0);

      const maskDataUrl = finalCanvas.toDataURL('image/png');
      downloadDataUrl(maskDataUrl, `canvas-mask-${Date.now()}.png`);
    } catch (error) {
      console.error('Mask generation error:', error);
      setErrorMessage('Unable to generate mask');
    }
  };

  // Handle component selection from search results
  const handleSelectSearchResult = (result) => {
    setSelectedImage(result.link);
    setImageSource('search');
    // Reset extraction state
    setBrushStrokes([]);
    setSamMasks([]);
    setSelectedSamMasks([]);
  };

  // Extract component using current selection method (brush or SAM)
  const handleExtractComponent = async () => {
    if (!selectedImage) {
      setErrorMessage('Please select an image first');
      return;
    }
    
    if (brushStrokes.length === 0 && selectedSamMasks.length === 0) {
      setErrorMessage('Please create a mask using brush or SAM tool first');
      return;
    }
    
    try {
      setLoading(true);
      setLoadingMessage('Extracting component...');
      const { dataUrl, width, height } = await createMaskedComponent(selectedImage, brushStrokes);
      const title = imageSource === 'search' ? 'Search result' : 'Extracted component';

      // Add to component cache
      const cacheItem = {
        id: `cache_${Date.now()}`,
        image: dataUrl,
        label: title,
        width,
        height,
        createdAt: Date.now()
      };
      setComponentCache(prev => [cacheItem, ...prev]);

      handleAddComponentToCanvas(dataUrl, title, { width, height });

      setSelectedImage(null);
      setImageSource(null);
      setBrushStrokes([]);
      setSamMasks([]);
      setSelectedSamMasks([]);
    } catch (error) {
      console.error('Component extraction failed', error);
      setErrorMessage(error.message || 'Failed to extract component. Try again or use a different image.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Toggle brush mode
  const toggleBrushMode = () => {
    setBrushMode(prev => !prev);
  };

  // Handle brush stroke on uploaded image
  const handleBrushStroke = (strokePath) => {
    setBrushStrokes(prev => [...prev, strokePath]);
  };

  // Generate SAM masks for the selected image
  const handleGenerateSamMasks = async () => {
    if (!selectedImage) {
      setErrorMessage('Please select an image first');
      return;
    }
    
    try {
      setLoading(true);
      setLoadingMessage('Generating SAM masks...');
      
      const response = await generateSamMasks(selectedImage);
      
      if (!response.success) {
        setErrorMessage(response.error || 'SAM mask generation failed');
        return;
      }
      
      setSamMasks(response.masks || []);
      setSelectedSamMasks([]);
      
    } catch (error) {
      console.error('SAM mask generation error:', error);
      setErrorMessage('Error generating SAM masks: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Toggle SAM mask selection
  const toggleSamMask = (maskId) => {
    setSelectedSamMasks(prev => {
      const isSelected = prev.includes(maskId);
      if (isSelected) {
        return prev.filter(id => id !== maskId);
      } else {
        return [...prev, maskId];
      }
    });
  };

  // Handle background generation with REVE
  const handleGenerateBackground = async () => {
    if (!canvasRef.current || !backgroundPrompt.trim()) {
      setErrorMessage('Canvas or prompt missing');
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage('Generating background with REVE...');

      // Get clean canvas data without any overlays
      const canvasDataUrl = canvasRef.current.getCleanDataURL
        ? canvasRef.current.getCleanDataURL()
        : canvasRef.current.toDataURL('image/png');

      console.log('Sending canvas to REVE...');

      const response = await inpaintImage(
        canvasDataUrl,
        [], // No component masks - send empty array as REVE doesn't need them
        backgroundPrompt,
        [], // No mask points
        [], // No SAM masks
        { seededit: true }, // Model selection flag
        null, // No brush mask
        0 // No mask blur
      );

      if (!response.success) {
        setErrorMessage(response.error || 'Background generation failed');
        return;
      }

      if (!response.images || response.images.length === 0) {
        setErrorMessage('Background generation returned no images. Please try again in a moment.');
        return;
      }

      console.log('REVE response:', response);

      if (response.images && response.images.length > 0) {
        const timestamp = Date.now();
        const newResults = response.images.map((image, index) => ({
          id: `reve_result_${timestamp}_${index}`,
          image,
          prompt: backgroundPrompt,
          createdAt: timestamp,
          metadata: response.reve || {}
        }));

        setBackgroundResults(prev => [...newResults, ...prev].slice(0, 12));
        setExpandedBackgroundIndex(0);
      }

    } catch (error) {
      console.error('REVE error:', error);
      setErrorMessage('Error generating background: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Handle component updates
  const handleUpdateComponentPosition = (index, x, y) => {
    setCanvasComponents(prev => prev.map((c, i) =>
      i === index ? { ...c, x, y } : c
    ));
  };

  const handleUpdateComponentSize = (index, width, height) => {
    setCanvasComponents(prev => prev.map((c, i) =>
      i === index ? { ...c, width, height } : c
    ));
  };

  const handleDeleteComponent = (index) => {
    setCanvasComponents(prev => prev.filter((_, i) => i !== index));
  };

  const handleComponentDrop = (component, x, y) => {
    const options = {
      x,
      y,
      width: component.width,
      height: component.height,
      rotation: component.rotation
    };
    handleAddComponentToCanvas(component.image || component.link, component.label || component.title, options);
  };

  return (
    <div className="min-h-screen bg-[#f6f1e8] text-[#352f26]">
        <ProgressBlinker isVisible={loading} message={loadingMessage} />
        
        <div className="mx-auto max-w-full px-6 py-8">
          {/* Error Message */}
          {errorMessage && (
            <motion.div 
              className="mb-6 glass backdrop-blur-md border border-red-300/40 bg-[#fce9e3] text-[#8b2f2f] px-4 py-3 rounded-xl"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {errorMessage}
            </motion.div>
          )}

          {/* Top Section - Add Components and Background Generation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Left - Add Components */}
            <Card variant="default" className="p-4 bg-transparent">
              <h3 className="text-lg font-semibold mb-3 text-[#463f33]">Compose from images</h3>
              <div className="space-y-4">
                {/* Search Section */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search Google for images..."
                      className="flex-1 p-2 border border-[#d7c7a6] rounded-lg text-sm bg-[#fdf6eb] placeholder:text-[#9c8d77] focus:outline-none focus:ring-2 focus:ring-[#d1c0a3]/60"
                      onKeyDown={(e) => e.key === 'Enter' && handleImageSearch()}
                    />
                    <Button 
                      variant="primary" 
                      onClick={handleImageSearch}
                      disabled={loading || !searchQuery.trim()}
                      className="px-3 py-2 text-sm"
                    >
                      Search
                    </Button>
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="max-h-32 overflow-y-auto border border-[#d8c6a7] rounded p-2 bg-[#f9f1e0]">
                      <div className="grid grid-cols-4 gap-1">
                        {searchResults.map((result, index) => (
                          <div 
                            key={index}
                            className="border border-[#d8c6a7] rounded p-1 cursor-pointer hover:bg-[#ede0c5] transition-colors"
                            onClick={() => handleSelectSearchResult(result)}
                          >
                            <img 
                              src={result.thumbnail} 
                              alt={result.title}
                              className="w-full h-12 object-cover rounded"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload or Drag & Drop */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <label
                      htmlFor="simple-editor-file-upload"
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg bg-[#efe1c4] text-[#4f4638] border border-[#d7c7a6] shadow-sm hover:bg-[#e8d7b9] cursor-pointer transition-colors"
                    >
                      Upload from disk
                    </label>
                    <input
                      id="simple-editor-file-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                  
                  <div 
                    className="border-2 border-dashed border-[#d6c4a3] rounded-lg p-4 text-center hover:border-[#bfa87f] transition-colors bg-[#fbf4e6]"
                    onDragOver={handleDragOver}
                    onDrop={handleDragDrop}
                  >
                    <p className="text-sm text-[#6b5e4a]">
                      Drag and drop an image from your library here
                    </p>
                  </div>
                </div>

                {/* Selected Image and Extraction Tools */}
                {selectedImage && (
                  <div className="space-y-3 border border-[#d8c6a7] rounded-lg p-3 bg-[#fcf5e9]">
                    <div className="flex gap-3">
                      <BrushableImage 
                        src={selectedImage} 
                        brushMode={brushMode}
                        brushSize={brushSize}
                        onBrushStroke={handleBrushStroke}
                        onBrushSizeChange={setBrushSizePx}
                        className="w-24 h-18 object-contain rounded border"
                      />
                      
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium text-[#514838]">
                          {imageSource === 'search' ? 'Search result' : 
                           imageSource === 'drag' ? 'Dragged image' : 'Uploaded image'}
                        </p>

                        <div className="flex gap-1 flex-wrap">
                          <Button 
                            variant={brushMode ? "primary" : "secondary"}
                            onClick={toggleBrushMode}
                            className="text-xs px-2 py-1"
                          >
                            {brushMode ? 'Stop Brush' : 'Brush'}
                          </Button>
                          
                          <Button 
                            variant="secondary"
                            onClick={handleGenerateSamMasks}
                            disabled={loading}
                            className="text-xs px-2 py-1"
                          >
                            SAM
                          </Button>
                          
                          {(brushStrokes.length > 0 || selectedSamMasks.length > 0) && (
                            <Button 
                              variant="primary"
                              onClick={handleExtractComponent}
                              className="text-xs px-2 py-1"
                            >
                              Extract
                            </Button>
                          )}
                          
                          <Button 
                            variant="secondary"
                            onClick={() => {
                              setSelectedImage(null);
                              setImageSource(null);
                              setBrushStrokes([]);
                              setSamMasks([]);
                              setSelectedSamMasks([]);
                            }}
                            className="text-xs px-2 py-1"
                          >
                            Clear
                          </Button>
                        </div>

                        {brushMode && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs">Brush Size:</span>
                            <input
                              type="range"
                              min="1"
                              max="60"
                              value={brushSize}
                              onChange={(e) => setBrushSize(Number(e.target.value))}
                              className="w-16"
                            />
                            <span className="text-xs text-gray-600">
                              {brushSize} units
                              {brushSizePx ? ` (~${Math.round(brushSizePx)}px)` : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* SAM Masks Display */}
                    {samMasks.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-[#5b5142]">SAM Masks:</p>
                        <div className="flex gap-1 flex-wrap">
                          {samMasks.map((mask, index) => (
                            <div
                              key={mask.id || index}
                              className={`w-12 h-9 border-2 rounded cursor-pointer ${
                                selectedSamMasks.includes(mask.id || mask.image) 
                                  ? 'border-[#c4a778]' 
                                  : 'border-[#d8c6a7]'
                              }`}
                              onClick={() => toggleSamMask(mask.id || mask.image)}
                            >
                              <img 
                                src={mask.image} 
                                alt="SAM mask" 
                                className="w-full h-full object-contain rounded"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            </Card>

            {/* Right - Background Generation */}
            <Card variant="default" className="p-4 bg-transparent">
              <h3 className="text-lg font-semibold mb-3 text-[#463f33]">Background Generation</h3>
              <div className="space-y-3">
                <textarea
                  value={backgroundPrompt}
                  onChange={(e) => setBackgroundPrompt(e.target.value)}
                  placeholder="Describe the background you want to generate..."
                  className="w-full p-2 border border-[#d7c7a6] rounded-lg resize-none text-sm bg-[#fdf6eb] placeholder:text-[#9c8d77] focus:outline-none focus:ring-2 focus:ring-[#d1c0a3]/60"
                  rows={4}
                />
                <Button 
                  variant="primary" 
                  onClick={handleGenerateBackground}
                  disabled={loading || !backgroundPrompt.trim()}
                  className="w-full"
                >
                  Generate Background
                </Button>
                {backgroundResults.length > 0 && (
                  <div className="mt-4 border border-[#d7c7a6] rounded-xl bg-[#fdf6eb] p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold tracking-wide uppercase text-[#6b5e4a]">
                        Recent REVE backgrounds
                      </span>
                      <button
                        type="button"
                        onClick={handleClearBackgroundResults}
                        className="text-xs text-[#8a7a63] hover:text-[#5b4f3e] underline"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {backgroundResults.map((result, idx) => (
                        <button
                          key={result.id}
                          type="button"
                          onClick={() => setExpandedBackgroundIndex(idx)}
                          className={`flex-shrink-0 rounded-lg border transition-all duration-150 ${
                            expandedBackgroundIndex === idx
                              ? 'border-[#c4a778] ring-2 ring-[#c4a778]/60'
                              : 'border-[#d7c7a6] hover:border-[#c4a778]'
                          }`}
                        >
                          <img
                            src={result.image}
                            alt={`REVE background ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        </button>
                      ))}
                    </div>
                    {expandedBackgroundIndex !== null && backgroundResults[expandedBackgroundIndex] && (
                      <div className="mt-3 space-y-3">
                        <div className="border border-[#d7c7a6] rounded-xl overflow-hidden bg-white shadow-sm">
                          <img
                            src={backgroundResults[expandedBackgroundIndex].image}
                            alt="Selected REVE background preview"
                            className="w-full h-auto"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleApplyBackgroundToCanvas(backgroundResults[expandedBackgroundIndex].image)}
                          >
                            Apply to Canvas
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadBackgroundResult(backgroundResults[expandedBackgroundIndex].image)}
                          >
                            Download
                          </Button>
                        </div>
                        {backgroundResults[expandedBackgroundIndex].prompt && (
                          <p className="text-xs text-[#6b5e4a]">
                            <span className="font-semibold uppercase tracking-wide">Prompt:</span>{' '}
                            {backgroundResults[expandedBackgroundIndex].prompt}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Component Cache */}
          {componentCache.length > 0 && (
            <div className="w-full mb-6">
              <Card variant="default" className="p-4 bg-transparent">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-[#463f33]">Extracted Components</h3>
                  <button
                    type="button"
                    onClick={() => setComponentCache([])}
                    className="text-xs text-[#8a7a63] hover:text-[#5b4f3e] underline"
                  >
                    Clear All
                  </button>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {componentCache.map((cachedComponent) => (
                    <div
                      key={cachedComponent.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', JSON.stringify(cachedComponent));
                      }}
                      className="flex-shrink-0 cursor-move group"
                    >
                      <div className="border-2 border-[#d7c7a6] rounded-lg p-2 bg-[#fdf6eb] hover:border-[#c4a778] hover:shadow-md transition-all duration-150">
                        <img
                          src={cachedComponent.image}
                          alt={cachedComponent.label}
                          className="w-20 h-20 object-contain rounded"
                        />
                        <p className="text-xs text-center text-[#6b5e4a] mt-1 truncate w-20">
                          {cachedComponent.label}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#8a7a63] mt-2 italic">
                  Drag and drop components onto the canvas
                </p>
              </Card>
            </div>
          )}

          {/* Canvas - Full width */}
          <div className="w-full">
            <Card variant="subtle" className="p-6">
              <div
                className="bg-[#faf2e1] rounded-xl border-2 border-[#d6c4a3] shadow-inner overflow-hidden w-full"
                style={{ aspectRatio: CANVAS_ASPECT_RATIO, maxHeight: `${CANVAS_HEIGHT}px` }}
              >
                <SimpleCanvas
                  ref={canvasRef}
                  components={canvasComponents}
                  onUpdateComponentPosition={handleUpdateComponentPosition}
                  onUpdateComponentSize={handleUpdateComponentSize}
                  onDeleteComponent={handleDeleteComponent}
                  onComponentDrop={handleComponentDrop}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                <Button variant="secondary" size="sm" onClick={handleDownloadCanvas}>
                  Download Canvas
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDownloadMask}>
                  Download Mask
                </Button>
              </div>
            </Card>
          </div>
        </div>
    </div>
  );
}

export default SimpleMainPage;
